import { Head, Link } from "@inertiajs/react"
import { Plus } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"

type PostRow = {
  id: number
  title: string
  slug: string
  status: string
  published_at: string | null
  created_at: string
}

export default function AdminPostsIndex({ posts }: { posts: PostRow[] }) {
  return (
    <>
      <Head title="Blog posts">
        <meta name="description" content="Manage blog posts." />
        <meta property="og:title" content="Blog posts" />
        <meta property="og:description" content="Manage blog posts." />
      </Head>
      <AdminShell>
        <div className="flex flex-col gap-4 border-b border-hairline pb-6 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1>Blog</h1>
            <p className="mt-1">
              {posts.length} {posts.length === 1 ? "post" : "posts"}.
            </p>
          </div>
          <Button asChild>
            <Link href="/admin/posts/new">
              <Plus className="h-4 w-4" />
              New post
            </Link>
          </Button>
        </div>

        {posts.length === 0 ? (
          <p className="mt-10 text-ink-muted">No posts yet. Create your first one.</p>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-md border border-hairline">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline bg-surface text-left">
                  <th className="px-4 py-3 font-medium text-ink-muted">Title</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Status</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {posts.map((post) => (
                  <tr key={post.id} className="hover:bg-surface">
                    <td className="px-4 py-3">
                      <Link
                        href={`/admin/posts/${post.id}/edit`}
                        className="font-medium text-ink-display no-underline hover:text-accent-display"
                      >
                        {post.title}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      {post.status === "published" ? (
                        <Badge tone="accent">Published</Badge>
                      ) : (
                        <Badge tone="muted">Draft</Badge>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-ink-muted">
                      {formatDate(post.published_at ?? post.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminShell>
    </>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}
